/**
 * Icon Loader - Main Entry Point
 * 
 * This module orchestrates loading of all FontAwesome icons used in the application.
 * Icons are organized by feature/domain for better maintainability.
 * 
 * USAGE:
 *   Import and call loadIcons() once at app startup (typically in index.js or App.js)
 *   
 *   import loadIcons from './icons';
 *   loadIcons();
 * 
 * ARCHITECTURE:
 *   - Icons are split into feature-based modules (core, medical, calendar, etc.)
 *   - Each module registers its icons with FontAwesome's global library
 *   - Components continue using <FontAwesomeIcon icon={['fad', 'calendar']} /> unchanged
 *   - No component code changes required
 * 
 * MAINTENANCE:
 *   - To add a new icon: Find the appropriate feature module and add it there
 *   - To remove an icon: Remove from the feature module (search codebase first!)
 *   - To create new category: Create new feature.icons.js and import here
 * 
 * Last refactored: 2026-01-31
 */

import { loadCoreIcons } from './core.icons';
import { loadFileIcons } from './files.icons';
import { loadFolderIcons } from './folders.icons';
import { loadMedicalIcons } from './medical.icons';
import { loadCalendarIcons } from './calendar.icons';
import { loadUserIcons } from './users.icons';
import { loadMessagingIcons } from './messaging.icons';
import { loadAIIcons } from './ai.icons';
import { loadEditorIcons } from './editor.icons';
import { loadPaymentIcons } from './payment.icons';
import { loadBrandIcons } from './brands.icons';
import { loadNavigationIcons } from './navigation.icons';
import { loadActionIcons } from './actions.icons';
import { loadMiscIcons } from './misc.icons';

/**
 * Load all icons into FontAwesome library
 * 
 * Call this function once at application startup.
 * All icons will be registered globally and available throughout the app.
 * 
 * @returns {void}
 */
const loadIcons = () => {
  // Core UI icons (always needed)
  loadCoreIcons();
  
  // Feature-based icon modules
  loadFileIcons();
  loadFolderIcons();
  loadMedicalIcons();
  loadCalendarIcons();
  loadUserIcons();
  loadMessagingIcons();
  loadAIIcons();
  loadEditorIcons();
  loadPaymentIcons();
  loadBrandIcons();
  loadNavigationIcons();
  loadActionIcons();
  loadMiscIcons();
};

/**
 * Load icons for a specific feature (lazy loading support)
 * 
 * Use this when you want to defer loading icons until a feature is accessed.
 * Example: loadFeatureIcons('medical') when medical dashboard is opened.
 * 
 * @param {string} feature - Feature name (e.g., 'medical', 'calendar')
 * @returns {boolean} - true if feature exists and was loaded, false otherwise
 */
export const loadFeatureIcons = (feature) => {
  const loaders = {
    core: loadCoreIcons,
    files: loadFileIcons,
    folders: loadFolderIcons,
    medical: loadMedicalIcons,
    calendar: loadCalendarIcons,
    users: loadUserIcons,
    messaging: loadMessagingIcons,
    ai: loadAIIcons,
    editor: loadEditorIcons,
    payment: loadPaymentIcons,
    brands: loadBrandIcons,
    navigation: loadNavigationIcons,
    actions: loadActionIcons,
    misc: loadMiscIcons,
  };
  
  const loader = loaders[feature];
  if (loader) {
    loader();
    return true;
  }
  
  console.warn(`[Icons] Unknown feature: ${feature}`);
  return false;
};

/**
 * Get list of available icon feature modules
 * 
 * Useful for debugging or documentation purposes.
 * 
 * @returns {string[]} - Array of feature names
 */
export const getAvailableFeatures = () => {
  return [
    'core',
    'files',
    'folders',
    'medical',
    'calendar',
    'users',
    'messaging',
    'ai',
    'editor',
    'payment',
    'brands',
    'navigation',
    'actions',
    'misc',
  ];
};

export default loadIcons;
